import React, { useEffect, useState } from 'react';
import { Row, Col, Table, Form } from 'react-bootstrap';
import Pagination from 'react-js-pagination';
import { useTranslation, Trans } from 'react-i18next';
import flatten from 'helpers/flatten';
import { parseDateBR } from 'helpers/date';
import {
  CurrencyPlainText,
  CurrencyEuroPlainText,
  CurrencyDollarPlainText,
} from 'helpers/Currency';
import { DebounceInput } from 'react-debounce-input';
import ActionStatus from 'enums/ActionStatus';
import { api } from 'services';

const Listing = ({
  fields,
  data,
  filters,
  stateFilters,
  handleFilterChange,
  actions,
  all,
  filterFiscalYears,
  pagination
}) => {
  const { t } = useTranslation();
  const [fiscalYears, setFiscalYears] = useState([]);
  const [events, setEvents] = useState([]);

  const handlePageChange = (page) => handleFilterChange({ page });

  const statusName = (status_id) => {
    switch (status_id) {
      case ActionStatus.Draft:
        return 'draft';
      case ActionStatus.OnApproval:
        return 'on_approval';
      case ActionStatus.Disapproved:
        return 'disapproved';
      case ActionStatus.AwaitingArts:
        return 'awaiting_arts';
      case ActionStatus.ArtsApproval:
        return 'arts_approval';
      case ActionStatus.DisapprovedArts:
        return 'disapproved_arts';
      case ActionStatus.AwaitingComprovations:
        return 'awaiting_comprovations';
      case ActionStatus.ComprovationsApproval:
        return 'comprovations_approval';
      case ActionStatus.DisapprovedComprovations:
        return 'disapproved_comprovations';
      case ActionStatus.AwaitingReceipt:
        return 'awaiting_receipt';
      case ActionStatus.Finished:
        return 'finished';
      case ActionStatus.Cancelled:
        return 'canceled';
      case ActionStatus.OnExecution:
        return 'on_execution';
      case ActionStatus.ControllingApproval:
        return 'controlling_approval';
      case ActionStatus.Approved:
        return 'approved';
      case ActionStatus.CustomerApproval:
        return 'controlling_approval';
    }
  };

  const switchCurrencyType = (value, currencyId) => {
    let currencyType;

    if (!!value) {
      switch (currencyId) {
        case 1:
          currencyType = (
            parseFloat(value).toLocaleString('pt-BR',{style:'currency',currency:'BRL'})
          );
          break;
        case 2:
          currencyType = (
            parseFloat(value).toLocaleString('en',{style:'currency',currency:'USD'})
          );
          break;
        case 3:
          currencyType = (
            parseFloat(value).toLocaleString('eu',{style:'currency',currency:'EUR'})
          );
          break;
      }
    } else {
      currencyType = '--';
    }
    return currencyType;
  };

  useEffect(() => {
    api.get('company-rules/fiscal-years').then(({ data }) => {
      let fiscalYearsOptions = data.map((field) => {
        return <option value={field.fiscal_year}>{field.fiscal_year}</option>;
      });

      setFiscalYears(fiscalYearsOptions.reverse());

      api
        .get('task-groups/list-for-event')
        .then(({ data }) => setEvents(data[0].tasks));
    });
  }, []);

  return (
    <>
      <Row>
        <Col>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label>{t('label:records per page')}:</Form.Label>
                <Form.Control
                  as="select"
                  value={stateFilters.per_page}
                  onChange={(e) =>
                    handleFilterChange({ per_page: e.target.value, page: 1 })
                  }
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={3} className="ml-auto">
              <Form.Group>
                <Form.Label>{t('label:search')}:</Form.Label>
                <DebounceInput
                  element={Form.Control}
                  debounceTimeout={500}
                  value={stateFilters.search}
                  onChange={(e) =>
                    handleFilterChange({ search: e.target.value, page: 1 })
                  }
                />
              </Form.Group>
            </Col>

            {filterFiscalYears && (
              <Col md={2}>
                <Form.Group>
                  <Form.Label>{t('label:fiscal year')}:</Form.Label>
                  <Form.Control
                    as="select"
                    value={stateFilters.fiscal_year}
                    onChange={(e) =>
                      handleFilterChange({
                        fiscal_year: e.target.value,
                        page: 1,
                      })
                    }
                  >
                    <option value="">{t('label:all')}</option>;{fiscalYears}
                  </Form.Control>
                </Form.Group>
              </Col>
            )}
          </Row>
          <Row>
            <Col className="table-mobile-transform">
              <Table bordered hover responsive>
                <thead>
                  <tr>
                    {all ? (
                      <th className="text-center align-middle">Status</th>
                    ) : null}
                    {fields.map((field, k) => (
                      <th className="text-center align-middle" key={k}>
                        {t(`label:${field.name}`)}
                      </th>
                    ))}
                    <th width={130} className="text-center align-middle">
                      {t('label:actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((dt, k) => {
                    const data = flatten(dt);
                    return (
                      <tr key={k}>
                        {all ? (
                          <td className="text-center align-middle">
                            
                        <div className="mobile-hidden-table-head">Status: </div>
                            {t(`statuses:${statusName(data.status_id)}`)}
                          </td>
                        ) : null}
                        {fields.map((field, k) => {
                          let text;
                          switch (field.type) {
                            case 'string':
                              text = !!data[field.key] ? data[field.key] : '--';
                              break;
                            case 'date':
                              text = !!data[field.key]
                                ? parseDateBR(data[field.key])
                                : '--';
                              break;
                            case 'currency':
                              text = !!data[field.key] ? (
                                parseFloat(data[field.key]).toLocaleString('pt-BR',{style:'currency',currency:'BRL'})
                              ) : (
                                '--'
                              );
                              break;
                            case 'custom':
                              text = field.value(data, dt);
                              break;
                            case 'currency mask':
                              text = switchCurrencyType(
                                data[field.key],
                                data.currency_id
                              );
                              break;
                            case 'event type':
                              let eventName;
                              events.map((event) => {
                                if (event.id === dt.task_id) {
                                  eventName = event.name;
                                }
                              });
                              text = eventName;
                              break;
                            default:
                              text = data[field.key];
                              break;
                          }

                          if (!!field.trans) text = t(`${field.trans}:${text}`);

                          return (
                            <td className="text-center align-middle" key={k}>      
                              <div className="mobile-hidden-table-head">{t(`label:${field.name}`)}: </div>
                              {text}
                            </td>
                          );
                        })}
                        <td className="text-center align-middle">
                        <div className='mobile-transform-table-card-btn'>
                          {typeof actions !== 'undefined'
                            ? (() => {
                                const will_return = [];
                                actions.forEach((action, k) =>
                                  will_return.push(action(dt, k))
                                );

                                return will_return;
                              })()
                            : ''}
                        </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Col>
          </Row>
          <Row>
            <Col>
              <Trans i18nKey="label:interpolateShowingRecords">
                Showing {{ from: (pagination?pagination.from:data.from) || '0' }} to {{ to: (pagination?pagination.to:data.to) || '0' }}{' '}
                from {{ total: (pagination?pagination.total:data.total) }} records
              </Trans>
            </Col>
            <Col className="d-flex justify-content-end">
              <Pagination
                activePage={pagination?pagination.current_page:data.current_page}
                itemsCountPerPage={pagination?pagination.per_page:data.per_page}
                totalItemsCount={pagination?pagination.total:data.total}
                pageRangeDisplayed={5}
                onChange={handlePageChange}
                itemClass="page-item"
                linkClass="page-link"
                prevPageText="‹"
                nextPageText="›"
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default Listing;
